/** @jsx isolateComponent */

import { Animation, Colors, useClickOutside } from '@zapier/design-system';
import { ZapierLogo } from '@zapier/design-system-patterns';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { useEffect, useState } from 'react';
import { skipLinkHref, UniversalNavLinkType } from '../../constants';
import { useUniversalNavContext } from '../../hooks';
import SkipLink from '../SkipLink';
import type { UniversalTopbarProps } from '../UniversalTopbar';
import UniversalDynamicTopbarLink from './UniversalDynamicTopbarLink';

import AccountActions from '../../components/UniversalTopbar/AccountActions';
import SearchForm from '../../components/UniversalTopbar/SearchForm';
import SearchToggler from '../../components/UniversalTopbar/SearchToggler';
import { emitUserInteractionEvent } from '../../utils/avroEvents';

import type { Props as SearchFormProps } from '../../components/UniversalTopbar/SearchForm';
import { UNIVERSAL_TOPBAR_HEIGHT } from '../../constants';
import { isEmptyObject } from '../../utils/isEmptyObject';

export type Props = UniversalTopbarProps;
type LinksProps = Props & {
  hasCta?: boolean;
  isSearching: boolean;
};

const Styles = labelStyles('UniversalDynamicTopbar', {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: `${Colors.neutral100}`,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: `1px solid ${Colors.neutral300}`,
    },
  ],
  header: (
    { height, size }: Props,
    { hasSidebar }: { hasSidebar: boolean }
  ) => [
    {
      alignItems: 'stretch',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      width: '100%',
      backgroundColor: `${Colors.neutral100}`,
      height,
      padding: '0 20px',

      // Whenever there's a sidebar, and especially a sidebar toggle,
      // the toggle button itself takes up enough space to warrant
      // collapsing the padding on the left side.
      paddingLeft: hasSidebar ? 10 : 20,
    },
    !hasSidebar &&
      withModifier('hasMaxWidth', {
        maxWidth: 1200,
      }),
    withModifier(
      `${size}-${height}`,
      {
        small: {
          gap: 5,
          height,
          padding: '0 10px',
        },
        medium: {
          height,
          padding: '0 20px',
          // Whenever there's a sidebar, and especially a sidebar toggle,
          // the toggle button itself takes up enough space to warrant
          // collapsing the padding on the left side.
          paddingLeft: hasSidebar ? 10 : 20,
        },
      }[size]
    ),
  ],
  group: ({ size }: Props) => [
    {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      justifySelf: 'flex-end',
    },
    withModifier(
      size,
      {
        small: { gap: 5 },
        medium: { gap: 17 },
      }[size]
    ),
  ],
  mainNavigationContent: [
    {
      display: 'flex',
      alignItems: 'stretch',
      marginLeft: 25,
    },
  ],
  searchForm: (props: Props, { isSearching }: { isSearching: boolean }) => [
    {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: Animation.transitionValue,
      opacity: 0,
      pointerEvents: 'none',
      transform: 'scaleX(0)',
    },
    isSearching &&
      withModifier('searching', {
        opacity: 1,
        pointerEvents: 'auto',
        transform: 'scaleX(1)',
      }),
    props.isSearchFullWidth &&
      withModifier('fullWidth', {
        backgroundColor: `${Colors.neutral100}`,
        padding: '0 10px',
      }),
  ],
  searchToggler: ({ isSearching }: { isSearching: boolean }) => [
    {
      marginRight: -7,
      transition: Animation.transitionValue,
    },
    isSearching &&
      withModifier('hidden', {
        opacity: 0.2,
        pointerEvents: 'none',
      }),
  ],
  navLinksList: (props: LinksProps) => [
    {
      display: 'grid',
      ...(props.links && {
        // Accounts for static product tab whose hard-coded data won't be in props.links
        gridTemplateColumns: `repeat(${props.links.length + 1}, max-content)`,
      }),
    },
    props.isSearching &&
      withModifier('searching', {
        opacity: 0,
      }),
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

export const staticProductTabBottomLinks: UniversalNavLinkType[] = [
  {
    text: 'Contact Sales',
    icon: 'actionCalendar',
    href: '/l/contact-sales?demo_source=cs_nav_menu_product',
  },
  {
    text: 'Explore app integrations',
    icon: 'actionExplore',
    href: '/apps',
  },
];

const Links = (props: LinksProps) => {
  const [activeMenu, setActiveMenu] = useState<string>('');
  const [activeElement, setActiveElement] = useState<Element | null>(null);

  // Clicking outside navigation area closes menu
  const [setClickOutsideNav] = useClickOutside({
    onClickOutside: () => setActiveMenu(''),
  });

  const toggleMegaMenu = (menuId: string) => {
    if (activeMenu === menuId) {
      if (activeElement instanceof HTMLElement) activeElement.focus();
      setActiveMenu('');
    } else {
      if (activeElement instanceof HTMLElement)
        setActiveElement(document.activeElement);
      setActiveMenu(menuId);
    }
  };

  const closeMenu = () => setActiveMenu('');

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (activeElement instanceof HTMLElement) activeElement.focus();
      setActiveMenu('');
    }
  };

  // escape key closes menu
  useEffect(() => {
    document.addEventListener('keydown', handleEscape, { capture: false });

    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: false });
    };
  });

  const filteredBottomLinks = staticProductTabBottomLinks.filter((link) => {
    return props.hasCta ? link.text !== 'Contact Sales' : link;
  });

  const staticProductTab = (
    <UniversalDynamicTopbarLink
      ariaControls="Product"
      bottomLinks={filteredBottomLinks}
      href={null}
      isMenuOpen={activeMenu === 'Product'}
      isStaticProductTab={true}
      key="Product"
      isSelected={props.selectedHref === 'Product'}
      onCloseMenu={closeMenu}
      position="navigation"
      // Submenu must be defined in order to render MegaMenu inside UniversalDynamicTopbarLink
      submenu={[]}
      toggleMenu={() => toggleMegaMenu('Product')}
    >
      Product
    </UniversalDynamicTopbarLink>
  );

  const links = props.links?.map((link) => (
    <UniversalDynamicTopbarLink
      ariaControls={link.text}
      bottomLinks={link.bottomLinks}
      href={link.href}
      isMenuOpen={activeMenu === link.text}
      key={link.text}
      isSelected={props.selectedHref === link.href}
      isStaticProductTab={false}
      position="navigation"
      promoArea={link.promoArea}
      submenu={link.submenu}
      toggleMenu={() => toggleMegaMenu(link.text)}
      onCloseMenu={closeMenu}
    >
      {link.text}
    </UniversalDynamicTopbarLink>
  ));
  if (!links) {
    return null;
  }
  return (
    <nav aria-label="Site Links" id="site-nav" ref={setClickOutsideNav}>
      <ul css={Styles.navLinksList(props)}>
        {staticProductTab}
        {links}
      </ul>
    </nav>
  );
};

/**
 * @private
 * Renders universal dynamic navigation top bar with Zapier logo, search,
 * links and account actions.
 */
const UniversalDynamicTopbar = (props: Props) => {
  const {
    currentUrl,
    getFullyQualifiedURL,
    shouldRenderCallToAction,
    topNavData,
  } = useUniversalNavContext();
  const hasCta = !isEmptyObject(topNavData.callToAction);

  const sidebar = props.renderSidebar && props.renderSidebar(props);
  const logoSize = {
    width: 105,
    height: 28,
  };

  const searchInputId = 'universal-nav-app-search';
  // Track whether the user is currently searching.
  const [isSearching, setIsSearching] = useState(false);
  const onCloseSearch = () => {
    setIsSearching(false);
  };
  const searchForm = props.renderSearchForm({
    isSearching,
    searchInputId,
    onClose: onCloseSearch,
  });
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: (event: MouseEvent | TouchEvent) => {
      if (props.onClickOutside) props.onClickOutside(event);
    },
  });
  const { links } = props;
  const callToActionUrl =
    getFullyQualifiedURL(
      `${topNavData.callToAction?.href}?demo_source=cs_nav_header_${currentUrl}`
    ) || '';

  return (
    <div css={Styles.root} ref={(node) => setClickOutsideNode(node)}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        css={Styles.header(props, { hasSidebar: !!sidebar })}
        data-testid="universalTopbar"
        onClick={props.onClick}
      >
        <div css={Styles.group(props)}>
          {sidebar}
          {props.shouldRenderLogo && (
            <header css={Styles.logoLinksWrapper}>
              <a
                aria-label={props.logoAriaLabel}
                href={getFullyQualifiedURL(props.logoHref)}
                onClick={props.onLogoClick}
              >
                <ZapierLogo {...logoSize} />
              </a>
              <div css={Styles.skipLink}>
                <SkipLink href={props.skipLinkHref} />
              </div>
            </header>
          )}
        </div>
        <div css={Styles.mainNavigationContent}>
          {links && links.length > 0 && (
            <Links
              {...props}
              hasCta={hasCta}
              isSearching={isSearching}
              links={links}
            />
          )}
          {!!searchForm && (
            <div
              aria-label="Search apps"
              css={Styles.searchForm(props, { isSearching })}
              onBlur={() => {
                setIsSearching(false);
              }}
              onFocus={() => {
                setIsSearching(true);
                emitUserInteractionEvent({
                  interaction_name: searchInputId,
                  event_action: 'in_view',
                  option_selected: 'Universal Nav Search opened',
                });
              }}
              role="form"
            >
              {searchForm}
            </div>
          )}
        </div>
        <div
          aria-label="Manage your account and search apps"
          css={Styles.group(props)}
          role="form"
        >
          {!!searchForm && (
            <div
              css={Styles.searchToggler({ isSearching })}
              data-testid="searchToggler"
            >
              <SearchToggler
                id={searchInputId}
                isSearching={false}
                label="Search apps…"
              />
            </div>
          )}

          {topNavData.callToAction &&
            props.size !== 'small' &&
            shouldRenderCallToAction && (
              <UniversalDynamicTopbarLink
                href={callToActionUrl}
                isSelected={false}
                position="actions-menu"
              >
                {topNavData.callToAction.text}
              </UniversalDynamicTopbarLink>
            )}

          {props.renderAccountMenu(props)}
        </div>
      </div>
    </div>
  );
};

UniversalDynamicTopbar.defaultProps = {
  logoAriaLabel: 'Go to the Zapier Homepage',
  logoHref: '/',
  onLogoClick: () => {},
  renderAccountMenu: (props: Props) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications: () => null,
  renderSearchForm: (props: SearchFormProps) => (
    <SearchForm
      isSearching={props.isSearching}
      onClose={props.onClose}
      searchInputId={props.searchInputId}
    />
  ),
  selectedHref: '',
  shouldRenderLogo: true,
  height: UNIVERSAL_TOPBAR_HEIGHT,
  size: 'medium',
  skipLinkHref,
};

export default UniversalDynamicTopbar;
